import axios from 'axios';
import { isEtawahPortalHost } from '../utils/branchPortalHost';

function rootApi() {
  const env = process.env.REACT_APP_ROOT_API_URL;
  if (env && String(env).trim()) return String(env).replace(/\/$/, '');
  if (typeof window !== 'undefined' && isEtawahPortalHost()) {
    return `${window.location.origin.replace(/\/$/, '')}/api`;
  }
  return 'https://admin.a2ztak.in/api';
}

function assetBase() {
  const env = process.env.REACT_APP_ASSET_BASE;
  if (env && String(env).trim()) return String(env).replace(/\/$/,'');
  if (typeof window !== 'undefined' && isEtawahPortalHost()) {
    return window.location.origin.replace(/\/$/, '');
  }
  return 'https://admin.a2ztak.in';
}

const ROOT_API_URL = rootApi();
/** Trailing slash so axios joins paths under /branch-manager/ correctly */
const BASE_URL = `${(process.env.REACT_APP_API_URL || `${ROOT_API_URL}/branch-manager`).replace(/\/$/, '')}/`;

export const ASSET_BASE = assetBase();

export function fileUrl(p) {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  const s = String(p).replace(/^\/api(\/uploads)/, '$1');
  return s.startsWith('/') ? `${ASSET_BASE}${s}` : `${ASSET_BASE}/${s.replace(/^\//, '')}`;
}

function cleanCustomerId(id) {
  return String(id || '').trim();
}

function attach(c) {
  const t = localStorage.getItem('etawah_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
}

function onErr(e) {
  if (e.response?.status === 401) {
    localStorage.removeItem('etawah_token');
    localStorage.removeItem('etawah_user');
    window.location.href = '/login';
  }
  return Promise.reject(e);
}

const api = axios.create({ baseURL: BASE_URL, timeout: 30000 });
api.interceptors.request.use(attach, (e) => Promise.reject(e));
api.interceptors.response.use((r) => r, onErr);

const longApi = axios.create({ baseURL: BASE_URL, timeout: 120000 });
longApi.interceptors.request.use(attach, (e) => Promise.reject(e));
longApi.interceptors.response.use((r) => r, onErr);

export const authAPI = {
  login: (username, password) =>
    axios.post(`${ROOT_API_URL}/users/login`, { username, password })
};

export const dashboardAPI = { get: () => api.get('dashboard') };
export const productsAPI = {
  list: (params) => api.get('products', { params }),
  getById: (id) => api.get(`products/${cleanCustomerId(id)}`),
  patchTransferPrice: (id, imeiIndex, data) =>
    api.patch(`products/${cleanCustomerId(id)}/imei/${imeiIndex}/transfer-price`, data)
};
export const salesAPI = {
  create: (data) => api.post('sales', data),
  list: (params) => api.get('sales', { params })
};
export const emiAPI = {
  list: (params) => api.get('emi', { params }),
  collect: (id, data) => api.post(`emi/${id}/collect`, data),
  remind: (id) => api.post(`emi/${id}/remind`),
  remindBulk: (body) => api.post('emi/remind-bulk', body || {})
};
export const customersAPI = {
  list: (params) => api.get('customers', { params }),
  create: (data) => api.post('customers', data),
  get: (id) => api.get(`customers/${cleanCustomerId(id)}`),
  patch: (id, data) => api.patch(`customers/${cleanCustomerId(id)}`, data)
};

function valuationUrl() {
  return typeof window !== 'undefined' && isEtawahPortalHost()
    ? `${window.location.origin.replace(/\/$/, '')}/api/branch-manager/old-mobile-valuation`
    : `${String(BASE_URL).replace(/\/$/, '')}/old-mobile-valuation`;
}

export const oldMobileValuationAPI = {
  suggest: (body) =>
    axios
      .post(valuationUrl(), body, {
        timeout: 75000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('etawah_token') || ''}`
        }
      })
      .catch(onErr)
};

export const localPurchaseAPI = { create: (body) => api.post('local-purchase', body) };
export const productFinalizeAPI = {
  finalize: (productId, body) =>
    api.patch(`products/${cleanCustomerId(productId)}/finalize-for-sale`, body)
};

export function uploadUnitRealPhotos(productId, imeiIndex, files) {
  const fd = new FormData();
  files.forEach((f) => fd.append('photos', f));
  return longApi.post(
    `products/${cleanCustomerId(productId)}/imei/${imeiIndex}/unit-real-photos`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
}

export const customersKycAPI = {
  upload: (id, formData) =>
    longApi.post(`customers/${cleanCustomerId(id)}/kyc`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

export const saleMediaAPI = {
  upload: (formData) =>
    longApi.post('sale-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getForCustomer: (customerId) =>
    api.get(`customers/${cleanCustomerId(customerId)}/sale-photos`)
};

export const reportAPI = {
  get: (date, kind = 'daily') => api.get('daily-report', { params: { date, kind } }),
  history: () => api.get('daily-report/history'),
  save: (data) => api.post('daily-report', { ...data, action: 'save' }),
  submit: (data) => api.post('daily-report', { ...data, action: 'submit' })
};

export const handoverAPI = {
  list: () => api.get('cash-handover'),
  create: (data) => api.post('cash-handover', data)
};

export const commissionAPI = {
  get: (month) => api.get('my-commission', { params: { month } }),
  timeline: (months) => api.get('commission-timeline', { params: { months } }),
  request: (amount, note) => api.post('commission-request', { amount, note }),
  requestStatus: () => api.get('commission-request/status')
};

export const analysisAPI = { get: (params) => api.get('analysis', { params }) };

export default api;
